/**
 * AppsFlyer attribution bridge — measurement for Meta app-install campaigns.
 *
 * Mucka is a hosted-WebView Capacitor app, so this web code runs inside the
 * native shell and can reach the AppsFlyer SDK through the Capacitor bridge.
 * On the plain web (and PWA) every function here is a silent no-op — paid
 * install attribution only makes sense in the store-installed app.
 *
 * The funnel mirrors the existing Amplitude taxonomy rather than inventing a
 * second one: `track()` forwards a small allowlist of events (see
 * forwardToAppsFlyer), and client-creation call-sites ping afClientCreated().
 * AppsFlyer relays these to Meta so campaigns can optimise on real activation
 * instead of raw installs.
 *
 * `af_activated` is the campaign optimisation event and deliberately fires on
 * EITHER a book-call tap OR a first client created. Team-size signups are
 * hardwalled into booking a call and never reach the in-app actions — if
 * activation only counted in-app work, Meta would learn to avoid showing ads
 * to multi-person businesses, our best segment. One event, two doors.
 *
 * Sends only happen in the prod env (the native shell loads app.mucka.ai) or
 * with NEXT_PUBLIC_DEBUG_APPSFLYER=1 for local testing against a dev build.
 */
import { hasNativePlugin, isIOS, isNative } from './is-native';
import { isProdEnv } from '@/lib/env/app-env';
import { ANALYTICS_EVENTS, type AnalyticsEventName } from '@/lib/analytics/events';

/** App Store numeric id for ai.mucka — public, ships in the binary anyway. */
const IOS_APP_ID = '6754921453';

/** Once-per-device guard for the af_activated optimisation event. */
const ACTIVATED_KEY = 'mucka_af_activated';

const AF_EVENTS = {
  /** Maps to Meta's CompleteRegistration standard app event. */
  SIGNUP_COMPLETED: 'af_complete_registration',
  /** Maps to Meta's TutorialCompletion standard app event. */
  ONBOARDING_COMPLETED: 'af_tutorial_completion',
  BOOK_CALL_CLICKED: 'af_book_call_clicked',
  CLIENT_CREATED: 'af_client_created',
  /** The merged activation event Meta campaigns optimise on. */
  ACTIVATED: 'af_activated',
} as const;

let initStarted = false;

/** One shared dynamic import so the SDK module loads exactly once. */
let pluginPromise: Promise<typeof import('appsflyer-capacitor-plugin')> | null = null;
function loadPlugin() {
  pluginPromise ??= import('appsflyer-capacitor-plugin');
  return pluginPromise;
}

function enabled(): boolean {
  return (
    isNative() &&
    !!process.env.NEXT_PUBLIC_APPSFLYER_DEV_KEY &&
    (isProdEnv() || process.env.NEXT_PUBLIC_DEBUG_APPSFLYER === '1')
  );
}

/**
 * Initialise the SDK and (iOS) raise the App Tracking Transparency prompt.
 * Call once at app start — AmplitudeProvider does. Safe to call repeatedly.
 */
export async function initAppsFlyer(): Promise<void> {
  if (!enabled() || initStarted) return;
  initStarted = true;
  // Old binary in the field: the AppsFlyer SDK first shipped in 3.2.0, but
  // this JS reaches every native user immediately. Pre-3.2.0 binaries can't
  // ever init — skip quietly (no plugin, no Sentry) so field monitoring isn't
  // drowned in expected "not implemented" errors while old versions linger.
  if (!hasNativePlugin('AppsFlyerPlugin')) return;
  try {
    // ATT prompt MUST come before initSDK. The plugin's initSDK promise only
    // resolves in the SDK's *start* completion, and any waitForATT option
    // defers start until ATT answers — so prompting after awaiting initSDK
    // deadlocks until the timeout and the prompt may never show. Asking first
    // means the consent answer is already settled when the SDK starts.
    if (isIOS()) await requestTrackingPermission();
    const { AppsFlyer } = await loadPlugin();
    await AppsFlyer.initSDK({
      devKey: process.env.NEXT_PUBLIC_APPSFLYER_DEV_KEY as string,
      appID: IOS_APP_ID,
      isDebug: !isProdEnv(),
    });
  } catch (e) {
    // Attribution must never break the app shell — but leave breadcrumbs we
    // can actually see: device consoles are unreachable in the field, so ship
    // the failure to Sentry too.
    console.warn('[appsflyer] init failed', e);
    reportAfError('appsflyer-init', e);
  }
}

/**
 * Native-SDK responses that just mean "the SDK is already started". Our JS
 * `initStarted` guard resets on every WebView page load, but the native
 * AppsFlyer SDK persists in the process — so a reload re-calls initSDK against
 * an already-started (or mid-start) SDK and it answers "SDK start already in
 * progress" or "Event timeout. Check `minTimeBetweenSessions` param" (code 10).
 * Expected on relaunch/reload, not actionable — ping only, no Sentry issue.
 * (Sentry MUCKA-WEB-2G / 2K / 2R / 2M.)
 *
 * The second group is the device failing to REACH AppsFlyer at all: no signal,
 * a van out of coverage, or a network that blocks appsflyersdk.com. iOS words it
 * "Network failure"; Android returns code 40 as either "Unable to resolve host"
 * or "Failed to connect to". Attribution retries on the next launch and nothing
 * is lost, so this says something about the customer's signal, not our code —
 * and it accounted for 3 of the 7 open web issues on 2026-08-12.
 * (Sentry MUCKA-WEB-2X / 3G / 3P.) Android's third wording of code 40 is
 * "Software caused connection abort" — ECONNABORTED, the connection dropping
 * part-way through the request, typically the phone switching between Wi-Fi
 * and mobile data as the app opens (MUCKA-WEB-AS, 28 Sep 2026).
 */
const AF_EXPECTED_ERRORS =
  /minTimeBetweenSessions|SDK start already in progress|Network failure|Unable to resolve host|No address associated with hostname|Failed to connect to|Software caused connection abort/i;

/** Exported for tests — the whole of `reportAfError`'s judgement lives here. */
export function isExpectedAfError(detail: string): boolean {
  return AF_EXPECTED_ERRORS.test(detail);
}

function reportAfError(stage: string, e: unknown): void {
  const detail = e instanceof Error ? `${e.name}: ${e.message}` : JSON.stringify(e);
  if (!isExpectedAfError(detail ?? '')) {
    void import('@sentry/nextjs')
      .then((Sentry) => {
        Sentry.captureException(e instanceof Error ? e : new Error(`${stage}: ${JSON.stringify(e)}`), {
          tags: { feature: 'appsflyer', stage },
        });
      })
      .catch(() => {});
  }
}

async function requestTrackingPermission(): Promise<void> {
  // Same old-binary story as initAppsFlyer — ATT also first shipped in 3.2.0.
  if (!hasNativePlugin('AppTrackingTransparency')) return;
  try {
    const { AppTrackingTransparency } = await import('capacitor-plugin-app-tracking-transparency');
    const { status } = await AppTrackingTransparency.getStatus();
    if (status === 'notDetermined') await AppTrackingTransparency.requestPermission();
  } catch (e) {
    // Declined / restricted / plugin missing — SKAN still measures in aggregate.
    console.warn('[appsflyer] ATT request failed', e);
    reportAfError('att-request', e);
  }
}

/**
 * Called by `track()` for every Amplitude event; forwards the few that matter
 * for ad optimisation. Keeping the mapping here (not at call-sites) means the
 * funnel stays in lockstep with the Amplitude taxonomy for free.
 */
export function forwardToAppsFlyer(event: AnalyticsEventName): void {
  if (!enabled()) return;
  switch (event) {
    case ANALYTICS_EVENTS.SIGNUP_COMPLETED:
      void logAfEvent(AF_EVENTS.SIGNUP_COMPLETED);
      break;
    case ANALYTICS_EVENTS.ONBOARDING_COMPLETED:
      void logAfEvent(AF_EVENTS.ONBOARDING_COMPLETED);
      break;
    case ANALYTICS_EVENTS.ONBOARDING_BOOK_CALL_CLICKED:
      void logAfEvent(AF_EVENTS.BOOK_CALL_CLICKED);
      markActivated('book_call');
      break;
    default:
      break;
  }
}

/**
 * Ping from every client-creation success path ('Client Created' itself is
 * tracked server-side, which AppsFlyer can't see). New creation flows should
 * call this too — it's one line and the activation guard makes it idempotent.
 */
export function afClientCreated(): void {
  if (!enabled()) return;
  void logAfEvent(AF_EVENTS.CLIENT_CREATED);
  markActivated('client_created');
}

function markActivated(door: 'book_call' | 'client_created'): void {
  try {
    if (localStorage.getItem(ACTIVATED_KEY)) return;
    localStorage.setItem(ACTIVATED_KEY, door);
  } catch {
    // Storage unavailable — fire anyway; a duplicate beats a lost conversion.
  }
  void logAfEvent(AF_EVENTS.ACTIVATED, { door });
}

async function logAfEvent(eventName: string, eventValue?: Record<string, string>): Promise<void> {
  try {
    const { AppsFlyer } = await loadPlugin();
    await AppsFlyer.logEvent({ eventName, eventValue: eventValue ?? {} });
  } catch {
    // Swallow — a failed attribution call must never surface to the user.
  }
}
